import React, { useState } from 'react';

/**
 * RuleTemplateParameterForm - Dynamic form for rule template parameters
 */
const RuleTemplateParameterForm = ({ template, onComplete, onCancel }) => {
    const [params, setParams] = useState(() => {
        // Initialize with default values from schema
        const initial = {};
        if (template.parameterSchemas) {
            template.parameterSchemas.forEach(schema => {
                initial[schema.name] = schema.defaultValue;
            });
        }
        return initial;
    });

    const handleChange = (name, value) => {
        setParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApply = () => {
        // Pass the customized rule back
        // We need to merge parameters into the rule definition
        const updatedRule = JSON.parse(JSON.stringify(template.ruleDefinition));

        // If it's a simple indicator, update top-level parameters
        if (updatedRule.conditionType === 'INDICATOR') {
            updatedRule.parameters = { ...updatedRule.parameters, ...params };
        }
        // If it's a composite, we might need a more complex mapping depending on how parameterSchemas are defined.
        // Assuming parameterSchemas currently map to top-level params or specific indicator params.
        // For now, let's just merge them.
        else if (updatedRule.conditionType === 'COMPOSITE') {
            // Simple merge for now, assuming schema matches structure
            // In a real scenario, schemas might have paths like "leftIndicator.parameters.period"
            Object.keys(params).forEach(key => {
                if (key.startsWith('left.')) {
                    updatedRule.leftIndicator.parameters[key.replace('left.', '')] = params[key];
                } else if (key.startsWith('right.')) {
                    updatedRule.rightIndicator.parameters[key.replace('right.', '')] = params[key];
                } else {
                    updatedRule[key] = params[key];
                }
            });
        }

        onComplete(updatedRule);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-1">
                <h3 className="font-bold text-lg">Customize Rule: {template.name}</h3>
                <p className="text-sm text-base-content/60">{template.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {template.parameterSchemas?.map(schema => (
                    <div key={schema.name} className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">{schema.description || schema.name}</span>
                        </label>
                        {schema.type === 'NUMBER' ? (
                            <input
                                type="number"
                                className="input input-bordered w-full"
                                value={params[schema.name] || ''}
                                onChange={(e) => handleChange(schema.name, Number(e.target.value))}
                                min={schema.minValue}
                                max={schema.maxValue}
                            />
                        ) : schema.type === 'STRING' ? (
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={params[schema.name] || ''}
                                onChange={(e) => handleChange(schema.name, e.target.value)}
                            />
                        ) : (
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={params[schema.name] || ''}
                                onChange={(e) => handleChange(schema.name, e.target.value)}
                            />
                        )}
                        {schema.unit && <label className="label"><span className="label-text-alt opacity-50">Unit: {schema.unit}</span></label>}
                    </div>
                ))}
            </div>

            <div className="modal-action">
                <button className="btn btn-ghost" onClick={onCancel}>Back to Library</button>
                <button className="btn btn-primary" onClick={handleApply}>Add to Strategy</button>
            </div>
        </div>
    );
};

export default RuleTemplateParameterForm;
